const API_BASE = 'https://balanced-arena.davidoberger.workers.dev';
const TOKEN_KEY = 'ba_auth_token';

// Token management
export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// Check URL for token (after OAuth redirect) and store it
export function checkUrlForToken(): boolean {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token) {
    setStoredToken(token);
    // Clean URL
    window.history.replaceState({}, '', window.location.pathname);
    return true;
  }
  return false;
}

// Decode HTML entities that may be in database from external sources
function decodeHTML(text: string): string {
  return text
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&ldquo;/g, '"')
    .replace(/&rdquo;/g, '"')
    .replace(/&lsquo;/g, "'")
    .replace(/&rsquo;/g, "'")
    .replace(/&ndash;/g, "–")
    .replace(/&mdash;/g, "—")
    .replace(/&hellip;/g, "…");
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include', // Keep for backwards compat
    headers: {
      ...headers,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// AUTH
// ============================================================================

export interface AuthUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  name: string;
  profilePictureUrl: string | null;
  playerId: string | null;
  playerName: string | null;
}

export interface Organization {
  id: string;
  name: string;
  type: string;
  role: string;
}

export interface AuthState {
  authenticated: boolean;
  user: AuthUser | null;
  organizations: Organization[];
}

export async function getAuthState(): Promise<AuthState> {
  return request<AuthState>('/api/auth/me');
}

export function getLoginUrl(returnTo?: string): string {
  const params = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : '';
  return `${API_BASE}/api/auth/login${params}`;
}

export function getSignupUrl(returnTo?: string): string {
  const params = new URLSearchParams();
  if (returnTo) params.set('return_to', returnTo);
  params.set('screen_hint', 'sign-up');
  return `${API_BASE}/api/auth/login?${params.toString()}`;
}

export async function logout(): Promise<void> {
  clearStoredToken();
  await request<{ success: boolean }>('/api/auth/logout', { method: 'POST' }).catch(() => {});
}

// Players
export async function createPlayer(name: string) {
  return request<{
    id: string;
    name: string;
    rating: { mu: number; sigma: number; ordinal: number };
    irt: { theta: number; standardError: number };
  }>('/api/players', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function getPlayer(id: string) {
  return request<{
    id: string;
    name: string;
    rating: { mu: number; sigma: number; ordinal: number };
    irt: { theta: number; standardError: number };
    stats: {
      gamesPlayed: number;
      questionsAnswered: number;
      correctAnswers: number;
      accuracy: number;
    };
  }>(`/api/players/${id}`);
}

export async function getLeaderboard(limit = 50) {
  return request<Array<{
    id: string;
    name: string;
    ordinal: number;
    rank: number;
    title: string;
    confidence: number;
  }>>(`/api/players?limit=${limit}`);
}

export async function findPlayerByName(name: string) {
  const leaderboard = await getLeaderboard(1000);
  return leaderboard.find(p => p.name.toLowerCase() === name.toLowerCase());
}

// Questions
export async function getQuestions(category?: string, limit = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (category) params.set('category', category);
  return request<Array<{
    id: string;
    text: string;
    category: string;
    difficulty: number;
  }>>(`/api/questions?${params}`);
}

export async function getCategories() {
  const categories = await request<Array<{ category: string; count: number }>>('/api/questions/meta/categories');
  // Decode HTML entities in category names
  return categories.map(c => ({ ...c, category: decodeHTML(c.category) }));
}

export interface CategoryTree {
  name: string;
  count: number;
  subcategories: Array<{ name: string; count: number }>;
}

export async function getCategoryTree() {
  const categories = await request<CategoryTree[]>('/api/questions/meta/categories/tree');
  // Decode HTML entities
  return categories.map(c => ({
    ...c,
    name: decodeHTML(c.name),
    subcategories: c.subcategories.map(s => ({
      ...s,
      name: decodeHTML(s.name),
    })),
  }));
}

export async function generateQuestions(type: string, limit = 20) {
  return request<{ generated: number; type: string }>(`/api/questions/generate/${type}?limit=${limit}`, {
    method: 'POST',
  });
}

// Interactive question types
export type QuestionType =
  | 'multiple_choice'
  | 'multiple_select'
  | 'true_false'
  | 'yes_no_maybe'
  | 'dropdown'
  | 'short_answer'
  | 'long_answer'
  | 'fill_blank'
  | 'cloze'
  | 'sequence'
  | 'matching'
  | 'categorization'
  | 'numeric'
  | 'range_slider'
  | 'likert'
  | 'rating'
  | 'hotspot'
  | 'label_diagram'
  | 'matrix_grid'
  | 'code_input'
  | 'chess_puzzle';

// Config interfaces for each interactive type
export interface ChessPuzzleConfig {
  fen: string;
  rating?: number;
  themes?: string[];
  puzzleId?: string;
}

export interface MultipleSelectConfig {
  options: string[];
  correctAnswers: string[];
}

export interface NumericConfig {
  unit?: string;
  decimalPlaces?: number;
}

export interface FillBlankConfig {
  textWithBlanks: string;
  acceptedAnswers: string[];
}

export interface ClozeConfig {
  textWithBlanks: string;
  blanks: Array<{ id: string; acceptedAnswers: string[] }>;
}

export interface SequenceConfig {
  items: string[];
}

export interface MatchingConfig {
  pairs: Array<{ left: string; right: string }>;
}

export interface CategorizationConfig {
  categories: Array<{ name: string; items: string[] }>;
}

export type InteractiveConfig =
  | ChessPuzzleConfig
  | MultipleSelectConfig
  | NumericConfig
  | FillBlankConfig
  | ClozeConfig
  | SequenceConfig
  | MatchingConfig
  | CategorizationConfig
  | Record<string, unknown>;

// Quiz API - Adaptive practice with per-category skill tracking
export interface QuizQuestion {
  id: string;
  text: string;
  imageUrl: string | null;
  answers?: string[];  // Optional for interactive questions
  answerImages?: (string | null)[];  // Optional for interactive questions
  category: string;
  subcategory: string | null;
  source: string | null;
  expectedProbability: number;
  difficulty: number;
  questionType?: QuestionType;
  interactiveConfig?: InteractiveConfig;
}

export interface QuizSession {
  sessionId: string;
  playerId: string;
  category: string;
  playerTheta: number;
  questions: QuizQuestion[];
  answerKey: Record<string, string>;
  explanationKey: Record<string, string | null>;
}

export interface QuizFilters {
  source?: string;
  state?: string;
  gradeLevel?: string;
  difficulty?: string;
}

export async function startQuiz(playerId: string, category?: string, subcategory?: string, count = 10, filters?: QuizFilters) {
  const session = await request<QuizSession>('/api/quiz/start', {
    method: 'POST',
    body: JSON.stringify({ playerId, category, subcategory, count, ...filters }),
  });
  // Decode HTML entities in questions and answers
  const decodedAnswerKey: Record<string, string> = {};
  for (const [id, answer] of Object.entries(session.answerKey)) {
    decodedAnswerKey[id] = decodeHTML(answer);
  }
  const decodedExplanationKey: Record<string, string | null> = {};
  for (const [id, explanation] of Object.entries(session.explanationKey)) {
    decodedExplanationKey[id] = explanation ? decodeHTML(explanation) : null;
  }
  return {
    ...session,
    category: decodeHTML(session.category),
    questions: (session.questions || []).map(q => ({
      ...q,
      text: decodeHTML(q.text),
      category: decodeHTML(q.category),
      // answers is undefined for interactive questions like chess puzzles
      answers: q.answers ? q.answers.map(decodeHTML) : undefined,
    })),
    answerKey: decodedAnswerKey,
    explanationKey: decodedExplanationKey,
  };
}

export interface AnswerResult {
  correct: boolean;
  correctAnswer: string;
  category: string;
  previousTheta: number;
  newTheta: number;
  newSE: number;
  questionsAnswered: number;
  correctAnswers: number;
  // For interactive questions (e.g., chess puzzles with partial credit)
  partialCredit?: number;
  feedback?: string;
}

export async function submitQuizAnswer(
  playerId: string,
  questionId: string,
  answer: string,
  responseTimeMs?: number
) {
  return request<AnswerResult>('/api/quiz/answer', {
    method: 'POST',
    body: JSON.stringify({ playerId, questionId, answer, responseTimeMs }),
  });
}

export interface CategorySkill {
  category: string;
  theta: number;
  standardError: number;
  questionsAnswered: number;
  correctAnswers: number;
  accuracy: number;
  skillLevel: string;
}

export async function getPlayerSkills(playerId: string) {
  return request<CategorySkill[]>(`/api/quiz/skills/${playerId}`);
}

// Matches (for PvP - kept for future use)
export async function startMatch(player1Id: string, player2Id: string, questionsPerPlayer = 5, category?: string) {
  return request<{
    matchId: string;
    players: {
      player1: { id: string; name: string; theta: number };
      player2: { id: string; name: string; theta: number };
    };
    winProbability: { player1: number; player2: number };
    questions: {
      player1: Array<{ id: string; text: string; answers: string[]; category: string; expectedProbability: number }>;
      player2: Array<{ id: string; text: string; answers: string[]; category: string; expectedProbability: number }>;
    };
  }>('/api/matches', {
    method: 'POST',
    body: JSON.stringify({ player1Id, player2Id, questionsPerPlayer, category }),
  });
}

export async function submitAnswer(matchId: string, playerId: string, questionId: string, answer: string, responseTimeMs?: number) {
  return request<{
    correct: boolean;
    correctAnswer: string;
    newTheta: number;
    newSE: number;
  }>(`/api/matches/${matchId}/answer`, {
    method: 'POST',
    body: JSON.stringify({ playerId, questionId, answer, responseTimeMs }),
  });
}

export async function endMatch(matchId: string) {
  return request<{
    matchId: string;
    winner: { id: string; name: string } | null;
    isDraw: boolean;
    finalScore: { player1: number; player2: number };
    ratingChanges: {
      player1: { before: { mu: number; sigma: number }; after: { mu: number; sigma: number }; change: number };
      player2: { before: { mu: number; sigma: number }; after: { mu: number; sigma: number }; change: number };
    };
  }>(`/api/matches/${matchId}/end`, { method: 'POST' });
}
